import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Globe2, Truck, Award, TrendingUp } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { BusinessCard, StatCard } from '@/components/Cards';
import { useLanguage } from '@/hooks/useLanguage';
import { ROUTE_PATHS } from '@/lib/index';

const f = { initial:{opacity:0,y:24}, whileInView:{opacity:1,y:0}, viewport:{once:true} };

const businessItems = [
  {
    title: 'Global Minerals & Metals',
    titleTh: 'แร่ธาตุและโลหะระดับโลก',
    description: 'Aluminum, Copper, Tungsten & bulk commodities — integrated global sourcing and full-chain logistics from SE Asia, Africa and beyond.',
    descriptionTh: 'อะลูมิเนียม ทองแดง ทังสเตน และสินค้าโภคภัณฑ์ — การจัดซื้อทั่วโลกและโลจิสติกส์ห่วงโซ่เต็มรูปแบบ',
    image: '/images/real_copper_sheets.webp',
    path: ROUTE_PATHS.BUSINESS_MINERALS,
    icon: '💎',
  },
  {
    title: 'Medical Devices',
    titleTh: 'อุปกรณ์การแพทย์',
    description: 'Class I medical devices: shower chairs, crutches, rollator walkers, and toilet aid equipment.',
    descriptionTh: 'อุปกรณ์การแพทย์ Class I: เก้าอี้อาบน้ำ ไม้เท้า วอล์กเกอร์มีล้อ และอุปกรณ์ช่วยชำระร่างกาย',
    image: '/images/shower_chair_1.jpg',
    path: ROUTE_PATHS.BUSINESS_MEDICAL,
    icon: '🏥',
  },
  {
    title: 'Coffee Products',
    titleTh: 'ผลิตภัณฑ์กาแฟ',
    description: 'Premium Thai coffee beans and processed products for domestic and international markets.',
    descriptionTh: 'เมล็ดกาแฟไทยพรีเมียมและผลิตภัณฑ์แปรรูปสำหรับตลาดในประเทศและต่างประเทศ',
    image: '/images/coffee_4.jpeg',
    path: ROUTE_PATHS.BUSINESS_COFFEE,
    icon: '☕',
  },
  {
    title: 'Thai Fruits Export',
    titleTh: 'ส่งออกผลไม้ไทย',
    description: 'Fresh Thai tropical fruits exported to China and beyond, with GACC-registered orchards.',
    descriptionTh: 'ผลไม้เขตร้อนไทยสดส่งออกไปจีนและทั่วโลก จากสวนที่ขึ้นทะเบียน GACC',
    image: '/images/fruits_2.jpeg',
    path: ROUTE_PATHS.BUSINESS_FRUITS,
    icon: '🍈',
  },
  {
    title: 'AI Live Streaming',
    titleTh: 'ไลฟ์สดด้วย AI',
    description: 'AI-powered live streaming e-commerce connecting Thai products with Chinese consumers.',
    descriptionTh: 'อีคอมเมิร์ซไลฟ์สดด้วย AI เชื่อมต่อสินค้าไทยกับผู้บริโภคชาวจีน',
    image: '/images/ailive_7.jpeg',
    path: ROUTE_PATHS.BUSINESS_AI_LIVE,
    icon: '📡',
  },
];

const stats = [
  { value: '50k+', label: 'MT / Month Capacity', labelTh: 'ปริมาณ MT / เดือน', icon: '⚓' },
  { value: '4+', label: 'Mineral Categories', labelTh: 'หมวดแร่ธาตุ', icon: '💎' },
  { value: 'SGS', label: 'Quality Certified', labelTh: 'ผ่านการรับรองคุณภาพ', icon: '✅' },
  { value: '6', label: 'Global Regions', labelTh: 'ภูมิภาคทั่วโลก', icon: '🌏' },
];

const advantages = [
  {
    icon: <Globe2 className="w-8 h-8 text-primary" />,
    title: 'Thailand-China Bridge',
    titleTh: 'สะพานเชื่อมไทย-จีน',
    desc: 'Deep connections with Chinese markets through Xianpinda partnership and GACC compliance.',
    descTh: 'ความสัมพันธ์ลึกซึ้งกับตลาดจีนผ่านความร่วมมือกับ Xianpinda และการปฏิบัติตาม GACC',
  },
  {
    icon: <Award className="w-8 h-8 text-primary" />,
    title: 'Certified & Compliant',
    titleTh: 'ได้รับการรับรองและปฏิบัติตาม',
    desc: 'All products meet international standards with SGS/CCIC certifications.',
    descTh: 'สินค้าทั้งหมดตรงตามมาตรฐานสากลพร้อมการรับรอง SGS/CCIC',
  },
  {
    icon: <Truck className="w-8 h-8 text-primary" />,
    title: 'End-to-End Logistics',
    titleTh: 'โลจิสติกส์แบบครบวงจร',
    desc: 'Full supply chain from mine or farm to final destination across global ports.',
    descTh: 'ห่วงโซ่อุปทานครบวงจร ตั้งแต่เหมืองหรือฟาร์มจนถึงปลายทางทั่วโลก',
  },
  {
    icon: <CheckCircle2 className="w-8 h-8 text-primary" />,
    title: 'Thai Majority Owned',
    titleTh: 'บริษัทไทยถือหุ้นเสียงข้างมาก',
    desc: 'Proudly Thai-owned, operating with transparency, integrity, and local expertise.',
    descTh: 'บริษัทไทยที่ภาคภูมิใจ ดำเนินงานด้วยความโปร่งใส ซื่อสัตย์ และความเชี่ยวชาญในท้องถิ่น',
  },
];

const metalsData = [
  { icon: '🏗️', name: 'Aluminum', nameTh: 'อะลูมิเนียม', grade: 'A7/A00 Ingots · Alumina · Billets · Coils' },
  { icon: '🔶', name: 'Copper', nameTh: 'ทองแดง', grade: 'Grade A Cathodes · Wire Rods · Concentrates' },
  { icon: '⚙️', name: 'Tungsten', nameTh: 'ทังสเตน', grade: 'APT · Oxide · Carbide Powder · Metal Rods' },
  { icon: '💎', name: 'Other Minerals', nameTh: 'แร่ธาตุอื่นๆ', grade: 'Iron Ore · Manganese · Nickel · Bauxite' },
];

export default function Home() {
  const { t, lang } = useLanguage();

  return (
    <Layout>
      {/* Hero */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden">
        <img src="/images/hero_3.jpeg" alt="Bangkok skyline" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-primary/80 via-primary/65 to-primary/80" />
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full border border-white/10 opacity-30 animate-pulse" />
          <div className="absolute bottom-1/4 left-1/4 w-64 h-64 rounded-full border border-white/10 opacity-20 animate-pulse delay-1000" />
        </div>
        <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
          <motion.div initial={{opacity:0,y:-20}} animate={{opacity:1,y:0}} transition={{duration:0.6}}
            className="inline-flex items-center gap-2 bg-white/10 border border-white/20 text-white/90 text-sm font-medium px-4 py-2 rounded-full mb-8 backdrop-blur-sm">
            🇹🇭 {t('Thai Trading Company', 'บริษัทการค้าแห่งประเทศไทย')}
          </motion.div>
          <motion.h1 initial={{opacity:0,y:30}} animate={{opacity:1,y:0}} transition={{duration:0.7,delay:0.1}}
            className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            {lang === 'th' ? (
              <>รากเหง้าในไทย<br /><span className="text-accent">เชื่อมโยงจีน</span><br />สู่โลกกว้าง</>
            ) : (
              <>Rooted in Thailand,<br /><span className="text-accent">Bridging China,</span><br />Reaching the World</>
            )}
          </motion.h1>
          <motion.p initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.6,delay:0.25}}
            className="text-white/80 text-lg md:text-xl mb-10 max-w-2xl mx-auto">
            {t(
              'A premier trading group specializing in non-ferrous metals, bulk commodities, medical devices, Thai fruits, and AI e-commerce.',
              'กลุ่มการค้าชั้นนำที่เชี่ยวชาญด้านโลหะอโลหะ สินค้าโภคภัณฑ์ อุปกรณ์การแพทย์ ผลไม้ไทย และอีคอมเมิร์ซ AI'
            )}
          </motion.p>
          <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.6,delay:0.4}}
            className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={ROUTE_PATHS.BUSINESS_MINERALS}
              className="inline-flex items-center gap-2 bg-white text-primary font-semibold px-8 py-4 rounded-full hover:bg-white/90 transition-all shadow-lg hover:shadow-xl hover:gap-3">
              {t('Explore Metals & Commodities', 'สำรวจโลหะและสินค้าโภคภัณฑ์')}
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to={ROUTE_PATHS.CONTACT}
              className="inline-flex items-center gap-2 bg-transparent border-2 border-white text-white font-semibold px-8 py-4 rounded-full hover:bg-white/10 transition-all">
              {t('Contact Us', 'ติดต่อเรา')}
            </Link>
          </motion.div>
        </div>
        <motion.div animate={{y:[0,8,0]}} transition={{repeat:Infinity,duration:1.5}}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/60">
          <div className="w-6 h-10 border-2 border-white/40 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-2 bg-white/60 rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* ★ FEATURED — Non-ferrous Metals & Bulk Trade */}
      <section className="bg-slate-900 text-white py-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...f} className="flex items-center gap-3 mb-4">
            <span className="bg-accent text-slate-900 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-widest">
              {t('Core Business', 'ธุรกิจหลัก')}
            </span>
            <span className="text-slate-400 text-sm">#1 Priority</span>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Text */}
            <div>
              <motion.h2 {...f} transition={{delay:0.05}}
                className="text-3xl md:text-5xl font-bold leading-tight mb-6">
                {lang === 'th' ? (
                  <>โลหะอโลหะ<br />และสินค้าโภคภัณฑ์<br /><span className="text-accent">การค้าระดับโลก</span></>
                ) : (
                  <>Non-ferrous Metals<br />&amp; Bulk Commodities<br /><span className="text-accent">Global Trading</span></>
                )}
              </motion.h2>
              <motion.p {...f} transition={{delay:0.1}} className="text-slate-300 text-base leading-relaxed mb-8">
                {t(
                  'Yaruwat is an integrated non-ferrous metals and bulk commodities trading platform — from mine to market. We source aluminum, copper, tungsten, and other minerals across Southeast Asia, Africa, Central Asia, and South America, delivering to global buyers with full SGS/CCIC quality assurance and ICC-compliant documentation.',
                  'Yaruwat เป็นแพลตฟอร์มการค้าโลหะอโลหะและสินค้าโภคภัณฑ์แบบบูรณาการ จากเหมืองสู่ตลาด เราจัดหาอะลูมิเนียม ทองแดง ทังสเตน และแร่ธาตุอื่นๆ ทั่วเอเชียตะวันออกเฉียงใต้ แอฟริกา เอเชียกลาง และอเมริกาใต้'
                )}
              </motion.p>
              <motion.div {...f} transition={{delay:0.15}} className="grid grid-cols-2 gap-4 mb-8">
                {[
                  { v:'50k–100k MT', l:'Monthly Capacity', th:'กำลังรายเดือน' },
                  { v:'4+', l:'Mineral Categories', th:'หมวดแร่ธาตุ' },
                  { v:'SGS / CCIC', l:'Certified per Batch', th:'รับรองต่อล็อต' },
                  { v:'6 Regions', l:'Global Market Reach', th:'การเข้าถึงตลาดโลก' },
                ].map(s => (
                  <div key={s.l} className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="text-accent font-bold text-xl">{s.v}</div>
                    <div className="text-slate-400 text-xs mt-0.5">{t(s.l, s.th)}</div>
                  </div>
                ))}
              </motion.div>
              <motion.div {...f} transition={{delay:0.2}} className="flex flex-wrap gap-2 mb-8">
                {metalsData.map(m => (
                  <div key={m.name} className="flex items-center gap-1.5 bg-white/10 border border-white/10 rounded-full px-3 py-1.5 text-sm">
                    <span>{m.icon}</span>
                    <span className="font-medium">{t(m.name, m.nameTh)}</span>
                  </div>
                ))}
              </motion.div>
              <motion.div {...f} transition={{delay:0.25}}>
                <Link to={ROUTE_PATHS.BUSINESS_MINERALS}
                  className="inline-flex items-center gap-2 bg-accent text-slate-900 font-bold px-7 py-3.5 rounded-full hover:opacity-90 transition-all shadow-lg hover:gap-3">
                  {t('View Full Minerals Portfolio', 'ดูพอร์ตโฟลิโอแร่ธาตุทั้งหมด')}
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </motion.div>
            </div>

            {/* Image collage */}
            <div className="grid grid-cols-2 gap-3">
              <motion.div {...f} transition={{delay:0.1}} className="col-span-2 rounded-2xl overflow-hidden h-52">
                <img src="/images/real_copper_sheets.webp" alt="Copper plates" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
              </motion.div>
              <motion.div {...f} transition={{delay:0.15}} className="rounded-2xl overflow-hidden h-44">
                <img src="/images/mining_6.jpeg" alt="Open pit mine" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
              </motion.div>
              <motion.div {...f} transition={{delay:0.2}} className="rounded-2xl overflow-hidden h-44">
                <img src="/images/bulk_2.jpeg" alt="Cargo ship" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
              </motion.div>
              <motion.div {...f} transition={{delay:0.25}} className="rounded-2xl overflow-hidden h-44">
                <img src="/images/real_copper_sheets.webp" alt="Copper plates Zambia" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
              </motion.div>
              <motion.div {...f} transition={{delay:0.3}} className="rounded-2xl overflow-hidden h-44">
                <img src="/images/bulk_6.jpeg" alt="Port logistics" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
              </motion.div>
            </div>
          </div>

          {/* Bottom metal cards */}
          <motion.div {...f} transition={{delay:0.3}} className="mt-12 border-t border-white/10 pt-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {metalsData.map((m, i) => (
                <motion.div key={m.name} {...f} transition={{delay:0.3+i*0.07}}
                  className="border border-white/10 rounded-xl p-4 hover:border-accent/50 transition-colors">
                  <div className="text-3xl mb-2">{m.icon}</div>
                  <div className="font-bold text-white text-sm mb-1">{t(m.name, m.nameTh)}</div>
                  <div className="text-slate-400 text-xs leading-relaxed">{m.grade}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map(s => (
              <StatCard key={s.label} value={s.value} label={s.label} labelTh={s.labelTh} icon={s.icon} />
            ))}
          </div>
        </div>
      </section>

      {/* All Business Areas */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...f} className="text-center mb-12">
            <span className="text-primary font-semibold text-sm uppercase tracking-widest">
              {t('What We Do', 'สิ่งที่เราทำ')}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-2 mb-4">
              {t('Our Business Areas', 'ธุรกิจของเรา')}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t(
                'Five dynamic sectors — metals & commodities, medical devices, coffee, Thai fruits, and AI e-commerce.',
                'ห้าภาคธุรกิจที่มีพลวัต — โลหะและสินค้าโภคภัณฑ์ อุปกรณ์การแพทย์ กาแฟ ผลไม้ไทย และอีคอมเมิร์ซ AI'
              )}
            </p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {businessItems.map((item, i) => (
              <motion.div key={item.title} {...f} transition={{delay: i * 0.1}}
                className={i === 0 ? 'sm:col-span-2 lg:col-span-2' : ''}>
                <BusinessCard {...item} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Advantages */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...f} className="text-center mb-12">
            <span className="text-primary font-semibold text-sm uppercase tracking-widest">
              {t('Why Choose Us', 'ทำไมต้องเลือกเรา')}
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-2 mb-4">
              {t('Our Core Advantages', 'ข้อได้เปรียบหลักของเรา')}
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {advantages.map((adv, i) => (
              <motion.div key={adv.title} {...f} transition={{delay: i * 0.1}}
                className="bg-white rounded-2xl p-6 shadow-md border border-border/50 hover:shadow-lg transition-shadow">
                <div className="mb-4">{adv.icon}</div>
                <h3 className="font-bold text-foreground mb-2">{t(adv.title, adv.titleTh)}</h3>
                <p className="text-sm text-muted-foreground">{t(adv.desc, adv.descTh)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Global Sourcing Visual Strip */}
      <section className="py-16 bg-background overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...f} className="text-center mb-8">
            <h3 className="text-xl font-bold text-foreground">
              {t('Our Global Sourcing Network', 'เครือข่ายการจัดซื้อทั่วโลกของเรา')}
            </h3>
            <p className="text-muted-foreground text-sm mt-1">
              {t('From mines and ports across continents', 'จากเหมืองและท่าเรือทั่วทุกทวีป')}
            </p>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { img:'/images/mining_3.jpeg', label:'Mining', th:'การขุดเจาะ' },
              { img:'/images/aluminum_ingot_1.jpeg', label:'Metal Products', th:'ผลิตภัณฑ์โลหะ' },
              { img:'/images/bulk_5.jpeg', label:'Sea Freight', th:'การขนส่งทางเรือ' },
              { img:'/images/bulk_6.jpeg', label:'Port Logistics', th:'โลจิสติกส์ท่าเรือ' },
            ].map((item, i) => (
              <motion.div key={item.label} {...f} transition={{delay: i*0.08}}
                className="relative rounded-2xl overflow-hidden h-40 group">
                <img src={item.img} alt={item.label} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                <div className="absolute bottom-3 left-3 text-white text-xs font-semibold">{t(item.label, item.th)}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div {...f}>
            <TrendingUp className="w-12 h-12 mx-auto mb-4 opacity-80" />
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {t('Ready to Partner with Us?', 'พร้อมที่จะเป็นพันธมิตรกับเราหรือยัง?')}
            </h2>
            <p className="text-primary-foreground/80 text-lg mb-8">
              {t(
                "Let's explore opportunities — metals, commodities, or any of our five sectors.",
                'มาสำรวจโอกาสทางธุรกิจร่วมกัน ไม่ว่าจะเป็นโลหะ สินค้าโภคภัณฑ์ หรือภาคธุรกิจใดก็ตาม'
              )}
            </p>
            <Link to={ROUTE_PATHS.CONTACT}
              className="inline-flex items-center gap-2 bg-white text-primary font-semibold px-8 py-4 rounded-full hover:bg-white/90 transition-all shadow-lg hover:gap-3">
              {t('Get in Touch', 'ติดต่อเรา')}
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}
